<!--
  The one tone vocabulary (refresh_plan.md 1.3). Seven divergent chip
  vocabularies across the SPA collapse into this component: it reads label,
  colour and plate shape from state.js and invents nothing of its own.

  There is deliberately no colour/tone override prop and no icon-only mode.
  refresh_plan.md 1.3 is explicit on both counts: "the chip may not accept a
  raw color" and "the component has no icon-only mode, because state is
  never carried by color alone". Both are enforced by construction below,
  not by convention: there is no prop the CSS reads besides the plate class
  and the three --lw-c-chip-* colour variables that come from state.js's
  token stem, and the label always renders, plate or no plate.
-->
<!--
  entry is null for an axis/value pair state.js does not define (see the
  `entry` computed): that is a caller bug, and the guard below means the
  failure mode is rendering nothing rather than a plate that merely looks
  plausible.

  This comment lives outside <template> on purpose: a comment as the first
  child inside <template>, ahead of the single real root element, compiles
  to its own comment vnode under Vue 3 (dev builds keep template comments by
  default), which turns the render output into a two-node fragment and
  leaves `$el`/the mounted wrapper's root pointed at the comment instead of
  the <span>. Moving explanatory template-root comments up here keeps the
  compiled output single-rooted.
-->
<template>
  <span v-if="entry" class="lw-c-chip" :class="plateClass" :style="chipStyle">
    <!--
      The regulatory plate's small ring glyph, before the text (refresh_plan.md
      1.3 / tokens.generated.scss PLATE SHAPES banner: "regulatory: white disc
      with a red ring"). It is decoration on top of an already-worded chip, not
      a substitute for the word, so it is aria-hidden.
    -->
    <span v-if="entry.plate === 'regulatory'" class="lw-c-chip__ring" aria-hidden="true" />
    <span class="lw-c-chip__label">{{ displayLabel }}</span>
  </span>
</template>

<script>
import { AXES, stateEntry, valuesFor } from './state';

/**
 * Every value name across every axis, for the `value` prop's first line of
 * defense. It is written to read only its own prop's raw value, so it cannot
 * know which axis "value" is meant to belong to: Vue 3 does hand a prop
 * `validator` the rest of the resolved props as a second argument, but moving
 * the pair check there would weaken it, because a failed validator only warns
 * and the component still renders. This catches a value that is not a real
 * state on any axis; the pair check that actually reads valuesFor(axis) lives
 * in the `entry` computed below, where both props are available together AND
 * where returning null also stops the plate from rendering at all.
 */
const ALL_VALUES = new Set(AXES.flatMap(axis => valuesFor(axis)));

export default {
  name: 'LwChip',
  props: {
    axis: {
      type: String,
      required: true,
      validator: value => AXES.includes(value),
    },
    value: {
      type: String,
      required: true,
      validator: value => ALL_VALUES.has(value),
    },
    // Overrides the shared label text. Use only for copy that varies by
    // surface; the default comes from state.js so "Confirmed" reads the same
    // on the board, the load detail and the POD panel (refresh_plan.md 1.3).
    label: {
      type: String,
      default: null,
    },
  },
  computed: {
    // The single place both axis and value are known at once, so this is
    // where the real pair check happens: an axis/value pair that does not
    // exist in state.js is a caller bug, not a chip to render. It logs
    // through console.error rather than throwing, the same observable
    // surface a failed prop validator uses (see LwNotice.test.js's "custom
    // validator check failed" assertion for the sibling pattern in this
    // library), and returns null so the template's v-if renders nothing:
    // an unknown pair fails loudly instead of falling back to a plausible
    // looking plate.
    entry() {
      const found = stateEntry(this.axis, this.value);
      if (!found) {
        // Deliberate dev-facing warning surface for an axis/value pair
        // state.js does not define, not debug output.
        // eslint-disable-next-line no-console
        console.error(
          `[LwChip] axis "${this.axis}" has no state "${this.value}" (state.js). ` +
            `Valid values for "${this.axis}": ${valuesFor(this.axis).join(', ') || '(unknown axis)'}.`
        );
      }
      return found;
    },
    plateClass() {
      return this.entry ? `lw-c-chip--${this.entry.plate}` : '';
    },
    // The label prop text must survive uppercasing: CSS does the caps
    // transform (see the style block), never a mutated string, so a screen
    // reader still reads the real words (refresh_plan.md 1.3).
    displayLabel() {
      if (!this.entry) return '';
      return this.label !== null ? this.label : this.entry.label;
    },
    // Colour comes in as three custom properties bound from the entry's own
    // token stem. The stylesheet below reads only these three names, never a
    // --lw-<axis>-<value>-* name directly, which is what keeps the plate
    // shape (the CSS class) and the palette (these three variables)
    // independent of each other. A dark theme is therefore an attribute
    // swap: the token layer redefines --lw-custody-in-transit-fg and friends
    // under :root[data-lw-theme='dark'], and this component never branches
    // on theme at all.
    chipStyle() {
      if (!this.entry) return {};
      return {
        '--lw-c-chip-fg': `var(--lw-${this.entry.token}-fg)`,
        '--lw-c-chip-bg': `var(--lw-${this.entry.token}-bg)`,
        '--lw-c-chip-edge': `var(--lw-${this.entry.token}-edge)`,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-1);
  box-sizing: border-box;
  color: var(--lw-c-chip-fg);
  // The plate face: caps and tracked, per refresh_plan.md 1.3. font's
  // shorthand carries family/size/weight/line-height; letter-spacing and
  // text-transform are not part of the shorthand so they read their own
  // token roles.
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  text-transform: var(--lw-type-label-transform);
  white-space: nowrap;
  vertical-align: middle;
}

// `none` (telemetry: live, sync: synced) is deliberately unstyled here: no
// background, no radius, no padding, just the base colour above. A system
// that paints success loudly has nothing left to say when something is
// wrong (tokens.generated.scss PLATE SHAPES banner), so the quietest state
// in an axis gets literally no rule of its own.

.lw-c-chip--guide {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  padding: var(--lw-space-1) var(--lw-space-2);
  // The signature inset white rule (direction-1.html .plate--guide, ~line
  // 128): an edge-coloured ring plus a paper-coloured ring one pixel in.
  // --lw-bg-paper is a system constant, the same for every guide chip on
  // every axis, which is why it is read directly here instead of being one
  // of the three per-entry chip variables. Custody is the only axis this
  // plan and the token file allow to use this shape.
  box-shadow:
    inset 0 0 0 1px var(--lw-c-chip-edge),
    inset 0 0 0 2px var(--lw-bg-paper);
}

.lw-c-chip--guide-quiet {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  padding: var(--lw-space-1) var(--lw-space-2);
  box-shadow: inset 0 0 0 1px var(--lw-c-chip-edge);
}

.lw-c-chip--guide-dashed {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  // Dashed edge reads as provisional: stated, not yet countersigned
  // (tokens.generated.scss PLATE SHAPES banner). box-shadow cannot draw a
  // dashed line, so this is the one plate that uses a real border instead of
  // the inset-ring technique the other square plates share; box-sizing:
  // border-box on the base class keeps its footprint the same size as theirs.
  border: 1px dashed var(--lw-c-chip-edge); // hairline: no border-width token
  padding: var(--lw-space-1) var(--lw-space-2);
}

.lw-c-chip--guide-struck {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  padding: var(--lw-space-1) var(--lw-space-2);
  box-shadow: inset 0 0 0 1px var(--lw-c-chip-edge);
}

// The withdrawal is carried by the strike, not by colour (refresh_plan.md /
// state.js custody.cancelled comment), so the strike lands on the label
// span specifically rather than the whole plate, and would still survive if
// a ring glyph were ever added next to it.
.lw-c-chip--guide-struck .lw-c-chip__label {
  text-decoration: line-through;
}

.lw-c-chip--warning {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  // Weight 700 overrides --lw-type-label's 600: a human should look
  // (tokens.generated.scss PLATE SHAPES banner). Telemetry staleness owns
  // this shape and nothing else may borrow it (state.js telemetry comment).
  font-weight: 700;
  // Extra padding on the side the chamfer eats into, per refresh_plan.md 1.3.
  padding: var(--lw-space-1) var(--lw-space-4) var(--lw-space-1) var(--lw-space-2);
  // The chamfered top-right corner (direction-1.html .plate--warn, ~line
  // 133), built from the spacing scale instead of a literal pixel offset so
  // the cut and the padding it needs stay proportional to one token.
  clip-path: polygon(
    0 0,
    calc(100% - var(--lw-space-2)) 0,
    100% var(--lw-space-2),
    100% 100%,
    0 100%
  );
}

.lw-c-chip--regulatory {
  // Paper ground is part of the shape's own definition (tokens.generated.scss
  // PLATE SHAPES banner: "white disc with a red ring"), not a per-state
  // colour, so it reads --lw-bg-paper directly rather than the entry's own
  // --lw-c-chip-bg. The two happen to be the same value for today's two
  // regulatory states, but the shape's whiteness should not depend on that
  // coincidence continuing to hold for a future regulatory state.
  background: var(--lw-bg-paper);
  border-radius: var(--lw-radius-pill);
  padding: var(--lw-space-1) var(--lw-space-3);
  // The only round shape in the system, reserved for absence.
  box-shadow: inset 0 0 0 2px var(--lw-c-chip-edge);
}

.lw-c-chip__ring {
  // A small ring glyph has no token in the scale, the same exception class
  // as a hairline or the warning plate's clip-path corner (hard rule 2).
  // Sized off --lw-space-2 so it still tracks the type scale as a token
  // rather than a free-floating number; only the border thickness is a raw
  // literal.
  width: var(--lw-space-2);
  height: var(--lw-space-2);
  border-radius: var(--lw-radius-pill);
  border: 2px solid currentColor; // hairline: no border-width token
  box-sizing: border-box;
  flex: none;
}

.lw-c-chip--neutral {
  background: var(--lw-c-chip-bg);
  border-radius: var(--lw-radius-xs);
  padding: var(--lw-space-1) var(--lw-space-2);
}
</style>
